import { Row, Col, Typography, Table, Button, message } from "antd";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingMarginTracking,
  removeProcessingMarginTracking,
  saveProcessingMarginTracking,
} from "store/po/poSlice";

const MESSAGE_KEY = "SAVE_TRACKING";

const MarginTrackingSavePage = () => {
  useDocumentTitle("Save Margin Tracking");
  const { processingMarginTracking, status, error } = useAppSelector((state) => state.po);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingMarginTracking;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingMarginTracking());
      }
    }

    if (!Object.keys(processingMarginTracking).length) {
      navigate(`/${routes.PO.UPLOAD_MARGIN_TRACKING}`);
    }
  }, [dispatch, processingMarginTracking, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving Margin Tracking", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Margin Tracking saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
      navigate(`/${routes.PO.UPLOAD_MARGIN_TRACKING}`);
    } else if (status === "cancelling") {
      message.loading({
        content: "cancelling Margin Tracking",
        key: MESSAGE_KEY,
      });
    } else if (status === "idle" && !Object.keys(processingMarginTracking).length) {
      navigate(`/${routes.PO.UPLOAD_MARGIN_TRACKING}`);
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error, navigate, processingMarginTracking]);

  let content;
  const { headerMapping } = processingMarginTracking;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSaveTracking = () => {
    dispatch(saveProcessingMarginTracking());
  };

  const onCancelTracking = () => {
    dispatch(cancelProcessingMarginTracking());
  };

  if (!Object.keys(processingMarginTracking).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingMarginTracking.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSaveTracking}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancelTracking}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      ></Table>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Margin Tracking" />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};

export default MarginTrackingSavePage;
